import {
    CheckoutErrorName,
    CheckoutLifecycleEventPostMessageName,
    checkoutPostMessage,
    Platform,
} from '@eventbrite/checkout-postmessage-api';
import { track } from '@eventbrite/datalayer-library';
import { ThunkAction } from 'redux-thunk';
import { HeapEventName } from '../constants/heap/events';
import { getReloadAfterCheckout, getWidgetParentUrl } from '../selectors/app';
import { getEventId, getEventUrl, isSeries } from '../selectors/event';
import { getParentEventId } from '../selectors/event/pureSelectors';
import { getCalendarType } from '../selectors/infiniteCalendarSeries/infiniteCalendarSeries';
import { getTotalAmount } from '../selectors/order';
import { getOrderId } from '../selectors/order/typedOrderSelectors';
import { getSeriesId } from '../selectors/series';
import { CheckoutState } from '../state';
import { trackAbandonOrder } from './eventAnalytics';

/**
 * Regex to match checkout url
 *
 * http(s)://                           Optional
 * www.                                 Optional
 * eventbrite || evbqa(##) || evbdev    Required - One EB's domains
 * .com, .uk, .etcetera                 Required - TLD of length 2 or more
 * /e/event-1234                        Required - /e/ + Event name dash public id
 * ?params=true&more=false              Optional - Captures possible query params + end of string
 */
/*eslint-disable no-useless-escape */
const EB_URLS_REGEX = new RegExp(
    '^(https?://)?(www.)?(eventbrite|evbqa(\\d)*|evbdev).\\w{2,}/e/([\\w\\d]+-)+(\\d{4,})([?].+&?)?$',
);

const getPostMessageActionParamsFromState = (state: CheckoutState) => {
    const reloadAfterCheckout = getReloadAfterCheckout(state);
    const widgetParentUrl = getWidgetParentUrl(state);

    const orderId = getOrderId(state);
    const totalOrderValue = getTotalAmount(state);

    const canonicalEventUrl = getEventUrl(state);
    const eid = getEventId(state);
    const seriesId = getSeriesId(state);
    const isSeriesEvent = isSeries(state);
    const eventId = isSeriesEvent && seriesId ? seriesId : eid;

    return {
        canonicalEventUrl,
        eventId,
        orderId,
        reloadAfterCheckout,
        totalOrderValue,
        widgetParentUrl,
    };
};

export const notifyParentStartCheckout =
    (): ThunkAction<void, CheckoutState, void> => (_dispatch, getState) => {
        const { eventId, widgetParentUrl } =
            getPostMessageActionParamsFromState(getState());

        return checkoutPostMessage({
            messageName: CheckoutLifecycleEventPostMessageName.orderStart,
            eventId,
            widgetParentUrl,
        });
    };

export const notifyParentBackToTicketSelection =
    (): ThunkAction<void, CheckoutState, void> => (_dispatch, getState) => {
        const { eventId, widgetParentUrl } =
            getPostMessageActionParamsFromState(getState());

        return checkoutPostMessage({
            messageName:
                CheckoutLifecycleEventPostMessageName.backToTicketSelection,
            eventId,
            widgetParentUrl,
        });
    };

/**
 * Set the information to call the parent window to prevent page reload that gets the user out of the
 * checkout process
 *
 * @param action - Hash expected to be in the shape of: {disable: true} or be empty.
 */
export const notifyParentProtectCheckout =
    (
        action: { disable?: boolean } = {},
    ): ThunkAction<void, CheckoutState, void> =>
    (_dispatch, getState) => {
        const { eventId, widgetParentUrl } =
            getPostMessageActionParamsFromState(getState());

        return checkoutPostMessage({
            messageName: CheckoutLifecycleEventPostMessageName.protectCheckout,
            eventId,
            widgetParentUrl,
            additionalData: action,
        });
    };

const triggerReloadOnParentWindow = () => {
    window.parent.location.reload();
};

export const notifyParentWidgetModalClose =
    (pathName: string | null = null): ThunkAction<void, CheckoutState, void> =>
    (dispatch, getState) => {
        const { eventId, widgetParentUrl, reloadAfterCheckout } =
            getPostMessageActionParamsFromState(getState());
        const seriesParentId = getParentEventId(getState());

        if (pathName) {
            track({
                eventName: HeapEventName.modalCloseButtonClick,
                eventData: {
                    pathname: pathName,
                    isSeriesInfiniteCalendar: isSeries(getState()),
                    calendarType: getCalendarType(getState()),
                },
            });
            dispatch(trackAbandonOrder(pathName));
        }

        if (
            reloadAfterCheckout &&
            widgetParentUrl &&
            EB_URLS_REGEX.test(widgetParentUrl) &&
            widgetParentUrl === window.parent.location.href
        ) {
            triggerReloadOnParentWindow();
        }
        const message = {
            messageName: CheckoutLifecycleEventPostMessageName.widgetModalClose,
            eventId,
            widgetParentUrl,
        };
        const additionalData = seriesParentId
            ? { additionalData: { seriesParentId } }
            : {};

        return checkoutPostMessage({ ...message, ...additionalData });
    };

export const notifyParentTicketQuantityChange =
    (
        changedTicketData: Record<string, unknown>,
    ): ThunkAction<void, CheckoutState, void> =>
    (_dispatch, getState) => {
        const { eventId, widgetParentUrl } =
            getPostMessageActionParamsFromState(getState());
        return checkoutPostMessage({
            messageName:
                CheckoutLifecycleEventPostMessageName.ticketQuantityChange,
            eventId,
            widgetParentUrl,
            additionalData: changedTicketData,
        });
    };

export const notifyParentJoinWaitlist =
    (): ThunkAction<void, CheckoutState, void> => (_dispatch, getState) => {
        const { eventId, widgetParentUrl } =
            getPostMessageActionParamsFromState(getState());
        checkoutPostMessage({
            messageName: CheckoutLifecycleEventPostMessageName.joinWaitlist,
            eventId,
            widgetParentUrl,
        });
    };

export const notifyParentTicketSelectionSubmit =
    (): ThunkAction<void, CheckoutState, void> => (_dispatch, getState) => {
        const { eventId, widgetParentUrl } =
            getPostMessageActionParamsFromState(getState());
        return checkoutPostMessage({
            messageName:
                CheckoutLifecycleEventPostMessageName.ticketSelectionSubmit,
            eventId,
            widgetParentUrl,
        });
    };

export const notifyParentTicketSelectionLayoutStabilized =
    (contentHeight?: number): ThunkAction<boolean, CheckoutState, void> =>
    (_dispatch, getState) => {
        const { eventId, widgetParentUrl } =
            getPostMessageActionParamsFromState(getState());

        checkoutPostMessage({
            messageName:
                CheckoutLifecycleEventPostMessageName.ticketSelectionLayoutStabilized,
            eventId,
            widgetParentUrl,
            additionalData: { contentHeight },
        });

        return true;
    };

export const notifyParentOrderStatusPlaced =
    (): ThunkAction<Platform | undefined, CheckoutState, void> =>
    (_dispatch, getState) => {
        const { eventId, widgetParentUrl, orderId, totalOrderValue } =
            getPostMessageActionParamsFromState(getState());

        return checkoutPostMessage({
            messageName:
                CheckoutLifecycleEventPostMessageName.orderStatusPlaced,
            eventId,
            widgetParentUrl,
            additionalData: { orderId, totalOrderValue },
        });
    };

export const notifyParentOrderComplete =
    (): ThunkAction<void, CheckoutState, void> => (_dispatch, getState) => {
        const { eventId, widgetParentUrl, orderId, totalOrderValue } =
            getPostMessageActionParamsFromState(getState());

        return checkoutPostMessage({
            messageName: CheckoutLifecycleEventPostMessageName.orderComplete,
            eventId,
            widgetParentUrl,
            additionalData: { orderId, totalOrderValue },
        });
    };

export const notifyParentPayInComplete =
    (): ThunkAction<void, CheckoutState, void> => (_dispatch, getState) => {
        const { eventId, widgetParentUrl } =
            getPostMessageActionParamsFromState(getState());

        return checkoutPostMessage({
            messageName: CheckoutLifecycleEventPostMessageName.orderComplete,
            eventId,
            widgetParentUrl,
        });
    };

export const notifyParentOrderCreationError =
    (errorCode: string): ThunkAction<void, CheckoutState, void> =>
    (_dispatch, getState) => {
        const { eventId, widgetParentUrl } =
            getPostMessageActionParamsFromState(getState());

        checkoutPostMessage({
            additionalData: { error: errorCode },
            eventId,
            messageName:
                CheckoutLifecycleEventPostMessageName.orderCreationError,
            widgetParentUrl,
        });
    };

export const notifyParentOrderPlacingError =
    (error: CheckoutErrorName): ThunkAction<void, CheckoutState, void> =>
    (_dispatch, getState) => {
        const { eventId, widgetParentUrl } =
            getPostMessageActionParamsFromState(getState());

        checkoutPostMessage({
            additionalData: { error },
            eventId,
            messageName:
                CheckoutLifecycleEventPostMessageName.orderPlacingError,
            widgetParentUrl,
        });
    };

export const notifyParentExpressPaymentProcessingOrder =
    (): ThunkAction<void, CheckoutState, void> => (_dispatch, getState) => {
        const { eventId, widgetParentUrl } =
            getPostMessageActionParamsFromState(getState());

        checkoutPostMessage({
            eventId,
            messageName:
                CheckoutLifecycleEventPostMessageName.expressPaymentProcessingOrder,
            widgetParentUrl,
        });
    };

export const notifyParentExpressPaymentCanceled =
    (): ThunkAction<void, CheckoutState, void> => (_dispatch, getState) => {
        const { eventId, widgetParentUrl } =
            getPostMessageActionParamsFromState(getState());

        checkoutPostMessage({
            eventId,
            messageName:
                CheckoutLifecycleEventPostMessageName.expressPaymentCanceled,
            widgetParentUrl,
        });
    };

export const notifyParentInitialPageFullyLoaded =
    (): ThunkAction<void, CheckoutState, void> => (_dispatch, getState) => {
        const { eventId, widgetParentUrl } =
            getPostMessageActionParamsFromState(getState());

        checkoutPostMessage({
            eventId,
            messageName:
                CheckoutLifecycleEventPostMessageName.initialPageFullyLoaded,
            widgetParentUrl,
        });
    };
